import type { CredentialCheckStatus } from "../types/credentials";
import { SQL_SIGN_IN_REFUSED_LABEL, isSqlSignInCheck } from "../connectors/sqlSignInRefusal";
import { DBT_REPO_SETTINGS_REFUSED_LABEL, isDbtRepoSettingsRefusal } from "./dbtRepoCheck";
import { CONNECTION_SETTINGS_REFUSED_LABEL, isConnectionSettingsRefusal } from "./settingsRefusal";

/**
 * Expiry arithmetic shared by the runner's daily check and the screens (2026-09-28 cred-check
 * design, "Result rules"). Pure; dates in, closed answers out.
 *
 * - 30 days or fewer left is a warning, once per secret version (`warned_at_days = 30`);
 * - 7 days or fewer is a warning, once (`7`);
 * - expired is a failure, once (`0`).
 *
 * A new secret version clears the stored expiry row, so `warned_at_days` starts again at null.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export type ExpiryThreshold = 30 | 7 | 0;

/** Whole days left, rounded down; negative once the date has passed. */
export function daysLeft(expiresAt: Date, now: Date): number {
  return Math.floor((expiresAt.getTime() - now.getTime()) / DAY_MS);
}

/** 0 once expired, 7 inside a week, 30 inside thirty days, null further out. */
export function expiryThreshold(expiresAt: Date, now: Date): ExpiryThreshold | null {
  const left = expiresAt.getTime() - now.getTime();
  if (left <= 0) return 0;
  if (left <= 7 * DAY_MS) return 7;
  if (left <= 30 * DAY_MS) return 30;
  return null;
}

/**
 * Whether a warning is due now: a threshold is reached that is lower than the last one warned
 * for this version. A date first seen inside a week warns once at 7, never again at 30.
 */
export function expiryWarningDue(
  threshold: ExpiryThreshold | null,
  warnedAtDays: number | null,
): boolean {
  if (threshold === null) return false;
  return warnedAtDays === null || threshold < warnedAtDays;
}

export type CredentialAttention =
  { kind: "refused" } | { kind: "expired" } | { kind: "expiring"; daysLeft: number };

/**
 * What a target needs a person to know, most urgent first: a refusal, then an expired date,
 * then the nearest date inside thirty days. An `unreachable` check is not a credential failure
 * (owner answer b2, 2026-09-28): it never needs attention on its own.
 */
export function credentialAttention(
  target: { status: CredentialCheckStatus; expiries: ReadonlyArray<{ expiresAt: Date }> },
  now: Date,
): CredentialAttention | null {
  if (target.status === "rejected") return { kind: "refused" };
  let nearest: Date | null = null;
  for (const expiry of target.expiries) {
    if (nearest === null || expiry.expiresAt < nearest) nearest = expiry.expiresAt;
  }
  if (nearest === null) return null;
  const threshold = expiryThreshold(nearest, now);
  if (threshold === 0) return { kind: "expired" };
  if (threshold === null) return null;
  return { kind: "expiring", daysLeft: daysLeft(nearest, now) };
}

/**
 * "Credentials refused", "Credentials expired", "Credentials expire in 6 days". #1097: a SQL
 * connection refused with cause `auth` reads "Sign in or settings refused" (`isSqlSignInCheck`),
 * since its database name or port can cause the same refusal. #1888: a connection refused with
 * `connection` or `missing_object` reads "Connection settings refused", as its email does
 * (`isConnectionSettingsRefusal`).
 */
export function credentialAttentionLabel(
  attention: CredentialAttention,
  target?: { targetType: string; connectorName?: string | null; causeCode?: string | null },
): string {
  switch (attention.kind) {
    case "refused":
      // #1674: a dbt repository address or branch that was not found is not a refused credential.
      if (target && isDbtRepoSettingsRefusal(target)) return DBT_REPO_SETTINGS_REFUSED_LABEL;
      if (target && isSqlSignInCheck(target)) return SQL_SIGN_IN_REFUSED_LABEL;
      // #1888: a connection refused about what it names reads as the email does.
      if (target && isConnectionSettingsRefusal(target)) return CONNECTION_SETTINGS_REFUSED_LABEL;
      return "Credentials refused";
    case "expired":
      return "Credentials expired";
    case "expiring":
      if (attention.daysLeft <= 0) return "Credentials expire today";
      return `Credentials expire in ${attention.daysLeft} ${attention.daysLeft === 1 ? "day" : "days"}`;
  }
}
